import React, { useState, useEffect, useCallback } from 'react';
import invoiceService from '../services/invoiceService';
import facilityService from '../services/facilityService';
import './InvoicesPage.css';

const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    facility_id: ''
  });
  const [pagination, setPagination] = useState({
    skip: 0,
    limit: 20
  });
  const [dailyReportDate, setDailyReportDate] = useState(new Date().toISOString().split('T')[0]);
  const [dailyReportFacility, setDailyReportFacility] = useState('');

  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        ...pagination,
        ...(filters.date_from && { date_from: filters.date_from }),
        ...(filters.date_to && { date_to: filters.date_to }),
        ...(filters.facility_id && { facility_id: filters.facility_id })
      };
      const data = await invoiceService.getInvoices(params);
      setInvoices(data.items);
      setTotal(data.total);
      setError('');
    } catch (err) {
      setError('Fehler beim Laden der Rechnungen: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination]);

  useEffect(() => {
    const loadFacilities = async () => {
      try {
        const data = await facilityService.getAllFacilities();
        setFacilities(data);
      } catch (err) {
        console.error('Error loading facilities:', err);
      }
    };
    loadFacilities();
  }, []);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, skip: 0 }));
  };

  const clearFilters = () => {
    setFilters({ date_from: '', date_to: '', facility_id: '' });
    setPagination(prev => ({ ...prev, skip: 0 }));
  };

  const handleDownloadPdf = async (invoice) => {
    try {
      const blob = await invoiceService.downloadPdf(invoice.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rechnung_${invoice.invoice_number.replace(/-/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Fehler beim Herunterladen: ' + err.message);
    }
  };

  const handleExportCsv = async () => {
    try {
      const params = {
        ...(filters.date_from && { date_from: filters.date_from }),
        ...(filters.date_to && { date_to: filters.date_to }),
        ...(filters.facility_id && { facility_id: filters.facility_id })
      };
      await invoiceService.exportCsv(params);
    } catch (err) {
      setError('Fehler beim CSV-Export: ' + err.message);
    }
  };

  const handleDailyReport = async () => {
    if (!dailyReportFacility) {
      setError('Bitte wählen Sie eine Anlage für den Tagesabschluss');
      return;
    }
    try {
      await invoiceService.getDailyReport(dailyReportDate, dailyReportFacility);
    } catch (err) {
      setError('Fehler beim Erstellen des Tagesabschlusses: ' + err.message);
    }
  };

  const handleDailyReportCsv = async () => {
    if (!dailyReportFacility) {
      setError('Bitte wählen Sie eine Anlage für den Tagesabschluss');
      return;
    }
    try {
      await invoiceService.getDailyReportCsv(dailyReportDate, dailyReportFacility);
    } catch (err) {
      setError('Fehler beim Erstellen des Kassenberichts: ' + err.message);
    }
  };

  const handlePageChange = (direction) => {
    setPagination(prev => ({
      ...prev,
      skip: direction === 'next'
        ? prev.skip + prev.limit
        : Math.max(0, prev.skip - prev.limit)
    }));
  };

  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;
  const totalPages = Math.ceil(total / pagination.limit);

  return (
    <div className="invoices-page">
      <header className="page-header">
        <div className="header-content">
          <div>
            <h1>Rechnungsarchiv</h1>
            <p>Rechnungen anzeigen, filtern und exportieren</p>
          </div>
          <button className="export-btn" onClick={handleExportCsv}>
            📥 CSV Export
          </button>
        </div>
      </header>

      {error && <div className="error-message">{error}</div>}

      <div className="daily-report-section">
        <h2>Tagesabschluss</h2>
        <div className="daily-report-form">
          <div className="filter-group">
            <label htmlFor="daily-report-date">Datum:</label>
            <input
              id="daily-report-date"
              type="date"
              value={dailyReportDate}
              onChange={(e) => setDailyReportDate(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label htmlFor="daily-report-facility">Anlage:</label>
            <select
              id="daily-report-facility"
              value={dailyReportFacility}
              onChange={(e) => setDailyReportFacility(e.target.value)}
            >
              <option value="">Anlage wählen</option>
              {facilities.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>
          <button className="daily-report-btn" onClick={handleDailyReport}>
            📊 Tagesabschluss (PDF)
          </button>
          <button className="daily-report-btn" onClick={handleDailyReportCsv}>
            📥 Kassenbericht (CSV)
          </button>
        </div>
      </div>

      <div className="filters-section">
        <div className="filters">
          <div className="filter-group">
            <label htmlFor="date-from">Von:</label>
            <input
              id="date-from"
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label htmlFor="date-to">Bis:</label>
            <input
              id="date-to"
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label htmlFor="facility-filter">Anlage:</label>
            <select
              id="facility-filter"
              value={filters.facility_id}
              onChange={(e) => handleFilterChange('facility_id', e.target.value)}
            >
              <option value="">Alle Anlagen</option>
              {facilities.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>
          <button className="clear-filters-btn" onClick={clearFilters}>Zurücksetzen</button>
        </div>
      </div>

      <div className="invoices-table-container">
        {loading ? (
          <div className="loading">Laden...</div>
        ) : (
          <>
            <table className="invoices-table">
              <thead>
                <tr>
                  <th>Rechnungsnr.</th>
                  <th>Datum</th>
                  <th>Kunde</th>
                  <th>Betrag</th>
                  <th>Zahlungsart</th>
                  <th>Anlage</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="no-data">Keine Rechnungen gefunden</td>
                  </tr>
                ) : (
                  invoices.map(inv => (
                    <tr key={inv.id}>
                      <td><span className="invoice-number">{inv.invoice_number}</span></td>
                      <td>{invoiceService.formatDate(inv.created_at)}</td>
                      <td>{inv.customer_name}</td>
                      <td>{invoiceService.formatCurrency(inv.total_amount)}</td>
                      <td>{invoiceService.getPaymentMethodLabel(inv.payment_method)}</td>
                      <td>{inv.facility_name}</td>
                      <td>
                        <button
                          className="action-btn download-btn"
                          onClick={() => handleDownloadPdf(inv)}
                          title="PDF herunterladen"
                        >
                          📄
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {total > pagination.limit && (
              <div className="pagination">
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange('prev')}
                  disabled={pagination.skip === 0}
                >
                  ← Zurück
                </button>
                <span className="pagination-info">
                  Seite {currentPage} von {totalPages} ({total} Rechnungen)
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange('next')}
                  disabled={pagination.skip + pagination.limit >= total}
                >
                  Weiter →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default InvoicesPage;
